import { urlToPattern } from "./urlToPattern";

type AtlassianProduct = "jira" | "confluence";

export function atlassianGatewayBaseUrl(
  product: AtlassianProduct,
  cloudId: string | undefined
) {
  const id = cloudId?.trim();
  return id ? `https://api.atlassian.com/ex/${product}/${id}` : "";
}

export function atlassianGatewayPattern(
  product: AtlassianProduct,
  cloudId: string | undefined
) {
  const baseUrl = atlassianGatewayBaseUrl(product, cloudId);
  return baseUrl
    ? urlToPattern(baseUrl)
    : `https://api\\.atlassian\\.com/ex/${product}/[^/]+/.*`;
}

export const atlassianServiceAccountOAuth = {
  grantType: () => "client_credentials" as const,
  tokenEndpoint: () => "https://auth.atlassian.com/oauth/token",
  tokenEndpointAuthMethod: () => "client_secret_post" as const,
  clientCredentialsFrom: "db" as const,
  scopes: () => [],
};

function atlassianCloudIdStep() {
  return {
    title: "Enter your Atlassian Cloud ID",
    items: [
      {
        kind: "input",
        label: "Cloud ID",
        placeholder: "1a11d016-8984-4c3e-b9ab-142dd06acb1b",
        required: true,
        target: { kind: "configuration", field: "cloudId" },
        helperText:
          "Your site’s Cloud ID is the value after /s/ in its Atlassian Administration URL. Don’t use the organization ID.",
      },
    ],
  } as const;
}

export function atlassianServiceAccountApiTokenSteps(
  productName: string,
  connectionTestScope: string
) {
  return [
    {
      title: "Create an API token for a service account",
      items: [
        {
          kind: "markdown",
          content: `Open [Atlassian Administration](https://admin.atlassian.com/), select your organization, then go to **Directory → Service accounts**. Create or select the service account, and give it only the ${productName} access and permissions it needs. Select **Create credentials → API token**.

Include the \`${connectionTestScope}\` scope used by the connection test, plus the scopes required by the connector’s requests. Copy the API token when Atlassian displays it; it can’t be viewed again.`,
        },
      ],
    },
    {
      title: "Enter your service account credentials",
      items: [
        {
          kind: "input",
          label: "Service account email",
          placeholder: "bot@serviceaccount.atlassian.com",
          required: true,
          target: { kind: "configuration", field: "email" },
        },
        {
          kind: "input",
          label: "API token",
          required: true,
          target: { kind: "secrets", field: "apiToken" },
        },
      ],
    },
    atlassianCloudIdStep(),
  ] as const;
}

export function atlassianServiceAccountOAuthSteps(
  productName: string,
  connectionTestScope: string
) {
  return [
    {
      title: "Create OAuth 2.0 credentials for a service account",
      items: [
        {
          kind: "markdown",
          content: `This option requires Atlassian’s centralized user management experience. Open [Atlassian Administration](https://admin.atlassian.com/), select your organization, then go to **Directory → Service accounts**. Create or select the service account, and give it only the ${productName} access and permissions it needs. Select **Create credentials → OAuth 2.0**.

Include the \`${connectionTestScope}\` scope used by the connection test, plus the scopes required by the connector’s requests. Copy the client ID and client secret when Atlassian displays them; they can’t be viewed again.`,
        },
      ],
    },
    {
      title: "Enter your OAuth 2.0 credentials",
      items: [
        {
          kind: "input",
          label: "Client ID",
          required: true,
          target: { kind: "oauthConnection", field: "clientId" },
        },
        {
          kind: "input",
          label: "Client secret",
          required: true,
          target: { kind: "oauthConnection", field: "clientSecret" },
        },
      ],
    },
    atlassianCloudIdStep(),
  ] as const;
}
